import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';

const SITE_URL = 'https://hoosierlifeinsurance.com';
const FORM_URL = 'https://readdy.ai/api/form/d7dsjpk5q56ra7tufqgg';

// ─── Checklist Data ───────────────────────────────────────────────────────────
interface ChecklistItem {
  id: string;
  text: string;
  detail: string;
  tip?: string;
  link?: { to: string; label: string };
}

interface ChecklistSection {
  id: string;
  phase: string;
  title: string;
  icon: string;
  color: string;
  bgColor: string;
  items: ChecklistItem[];
}

const SECTIONS: ChecklistSection[] = [
  {
    id: 'phase1',
    phase: 'Phase 1',
    title: 'Assess Your Needs',
    icon: 'ri-search-eye-line',
    color: 'text-[#1A3A52]',
    bgColor: 'bg-[#1A3A52]',
    items: [
      {
        id: 'p1-1',
        text: 'Calculate your total coverage need using the DIME method',
        detail: 'Add up: Debt (mortgage + loans + credit cards + final expenses) + Income replacement (annual income × years needed) + Mortgage (if not in Debt) + Education (college costs per child).',
        tip: 'Indiana average: families need 10–12× annual income. A $75K earner with 2 kids typically needs $1.2M–$1.5M.',
        link: { to: '/resources/indiana-life-insurance-calculator', label: 'Use our free calculator' },
      },
      {
        id: 'p1-2',
        text: 'List all existing life insurance coverage',
        detail: 'Include employer group life insurance (check your benefits portal), any individual policies you own, and any policies your spouse owns on you.',
        tip: 'Most Indiana employers offer only 1–2× salary. This is rarely enough — it also disappears if you change jobs.',
      },
      {
        id: 'p1-3',
        text: 'Identify your coverage gap',
        detail: 'Subtract your existing coverage and liquid savings from your total coverage need. This gap is the amount of new insurance you need to buy.',
      },
      {
        id: 'p1-4',
        text: 'Determine how long you need coverage',
        detail: 'Consider: years until your youngest child is financially independent (typically age 22), years until your mortgage is paid off, and years until retirement. Your term length should cover the longest of these.',
        tip: 'Most Indiana families with young children need a 20- or 30-year term policy.',
      },
      {
        id: 'p1-5',
        text: 'Decide if you need permanent coverage in addition to term',
        detail: 'Permanent coverage (whole life or IUL) makes sense for: final expense coverage, estate planning, business succession, or supplemental retirement savings. Most families start with term only.',
        link: { to: '/resources/term-vs-whole-life-indianapolis-homeowners', label: 'Term vs. Whole Life guide' },
      },
    ],
  },
  {
    id: 'phase2',
    phase: 'Phase 2',
    title: 'Gather Your Information',
    icon: 'ri-folder-open-line',
    color: 'text-[#D97642]',
    bgColor: 'bg-[#D97642]',
    items: [
      {
        id: 'p2-1',
        text: 'Collect your personal identification',
        detail: 'You\'ll need: full legal name, date of birth, Social Security number, driver\'s license number, and current address. Indiana carriers verify identity before issuing a policy.',
      },
      {
        id: 'p2-2',
        text: 'List all prescription medications (past 5–10 years)',
        detail: 'Underwriters check the MIB (Medical Information Bureau) and prescription drug databases. Be thorough — omissions can be flagged as misrepresentation.',
        tip: 'Include dosage and condition being treated. Controlled medications (Adderall, opioids, benzodiazepines) require extra documentation.',
      },
      {
        id: 'p2-3',
        text: 'Document all medical conditions and diagnoses',
        detail: 'Include: diabetes, heart disease, cancer history, mental health diagnoses, sleep apnea, high blood pressure, high cholesterol, and any surgeries or hospitalizations in the past 10 years.',
        tip: 'Well-controlled conditions often qualify for standard or preferred rates. Don\'t assume a condition disqualifies you.',
      },
      {
        id: 'p2-4',
        text: 'Note your height, weight, and BMI',
        detail: 'Carriers use build charts to assess risk. Indiana residents with BMI over 30 may face a table rating. Losing weight before applying can significantly improve your rate class.',
      },
      {
        id: 'p2-5',
        text: 'Gather your driving record information',
        detail: 'DUIs, reckless driving, or multiple moving violations in the past 3–5 years can affect your rate or result in a decline. Indiana carriers typically pull a 3-year MVR.',
        tip: 'A single DUI within 3 years typically results in a table rating or decline at most carriers.',
      },
      {
        id: 'p2-6',
        text: 'Document any hazardous hobbies or occupations',
        detail: 'Carriers ask about: scuba diving, skydiving, rock climbing, private piloting, motorcycle riding, and hazardous occupations (mining, logging, commercial fishing). These may add a flat extra premium.',
      },
      {
        id: 'p2-7',
        text: 'Know your tobacco and nicotine use history',
        detail: 'Carriers ask about cigarettes, cigars, chewing tobacco, vaping, nicotine patches, and marijuana. Most carriers require 12 months nicotine-free to qualify for non-tobacco rates.',
        tip: 'Quitting tobacco before applying can cut your premium by 50–60%. Indiana carriers test for cotinine (nicotine metabolite) in urine.',
      },
      {
        id: 'p2-8',
        text: 'Prepare your financial information',
        detail: 'For coverage over $1M, carriers may request: recent tax returns, W-2s, or financial statements to verify insurable interest and income replacement need.',
      },
    ],
  },
  {
    id: 'phase3',
    phase: 'Phase 3',
    title: 'Shop & Compare Carriers',
    icon: 'ri-store-2-line',
    color: 'text-teal-600',
    bgColor: 'bg-teal-600',
    items: [
      {
        id: 'p3-1',
        text: 'Work with an independent agent (not a captive agent)',
        detail: 'An independent agent can shop your case across 20+ carriers simultaneously. A captive agent (State Farm, Northwestern Mutual) can only offer their company\'s products. Independent agents almost always find better rates.',
        tip: 'Hoosier Life Insurance is an independent agency — we shop all major Indiana carriers for you at no extra cost.',
      },
      {
        id: 'p3-2',
        text: 'Get quotes from at least 3–5 different carriers',
        detail: 'Rates vary significantly between carriers for the same coverage. For a 40-year-old with diabetes, one carrier might charge $120/month while another charges $85/month for identical coverage.',
        link: { to: '/instant-quote', label: 'Get instant multi-carrier quotes' },
      },
      {
        id: 'p3-3',
        text: 'Verify each carrier\'s AM Best financial strength rating',
        detail: 'Only buy from carriers rated A- or better by AM Best. This ensures the company will be financially able to pay your claim decades from now. Top Indiana carriers: Banner Life (A+), Protective (A+), Mutual of Omaha (A+).',
        link: { to: '/resources/how-to-choose-life-insurance-company-indiana', label: 'Carrier selection guide' },
      },
      {
        id: 'p3-4',
        text: 'Compare the conversion privilege on term policies',
        detail: 'The conversion privilege lets you convert your term policy to permanent coverage without a medical exam. Check: the conversion deadline (some expire at age 65 or 70), and which permanent products you can convert to.',
        tip: 'This is critical if you develop a health condition during your term — it\'s your safety net for permanent coverage.',
      },
      {
        id: 'p3-5',
        text: 'Ask about accelerated underwriting options',
        detail: 'Many Indiana carriers now offer no-exam approval for healthy applicants up to $1M+ using data algorithms. This can get you approved in 24–72 hours instead of 4–6 weeks.',
      },
      {
        id: 'p3-6',
        text: 'Understand the difference between illustrated and guaranteed values',
        detail: 'For permanent policies (whole life, IUL), ask for both guaranteed and non-guaranteed illustrations. Only rely on guaranteed values when making your decision.',
      },
    ],
  },
  {
    id: 'phase4',
    phase: 'Phase 4',
    title: 'Complete the Application',
    icon: 'ri-file-edit-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-600',
    items: [
      {
        id: 'p4-1',
        text: 'Answer all application questions completely and honestly',
        detail: 'Material misrepresentation (false or incomplete answers) can result in policy cancellation or claim denial during the 2-year contestability period. When in doubt, disclose.',
        tip: 'Indiana follows the standard 2-year contestability rule. After 2 years, the policy is generally incontestable.',
      },
      {
        id: 'p4-2',
        text: 'Schedule and complete the paramedical exam (if required)',
        detail: 'The exam is free and conducted at your home or office by a nurse. It includes: blood draw, urine sample, blood pressure, height/weight, and health history questions. Fast 20–30 minutes.',
        tip: 'For best results: fast for 8 hours before, avoid alcohol 24 hours before, drink plenty of water, and schedule in the morning.',
      },
      {
        id: 'p4-3',
        text: 'Authorize release of medical records (APS)',
        detail: 'The carrier may request an Attending Physician Statement (APS) from your doctor for certain conditions. This can add 2–4 weeks to the underwriting process.',
      },
      {
        id: 'p4-4',
        text: 'Name your primary and contingent beneficiaries',
        detail: 'Primary beneficiary: who gets the death benefit first (usually spouse). Contingent beneficiary: backup if primary predeceases you (usually children or a trust). Always name both.',
        tip: 'If your children are minors, consider naming a trust as contingent beneficiary so a trustee manages the funds until they reach adulthood.',
      },
      {
        id: 'p4-5',
        text: 'Decide on the policy owner',
        detail: 'Usually you own your own policy. For estate planning purposes, a trust or spouse may own the policy to keep the death benefit out of your taxable estate. Consult an estate attorney for large policies.',
      },
      {
        id: 'p4-6',
        text: 'Choose your premium payment frequency',
        detail: 'Options: monthly, quarterly, semi-annual, or annual. Paying annually saves 5–8% compared to monthly payments with most Indiana carriers.',
        tip: 'Annual payment on a $60/month policy saves approximately $40–$60/year.',
      },
    ],
  },
  {
    id: 'phase5',
    phase: 'Phase 5',
    title: 'Review & Accept Your Policy',
    icon: 'ri-shield-check-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-600',
    items: [
      {
        id: 'p5-1',
        text: 'Review your underwriting decision and rate class',
        detail: 'The carrier will issue an offer with your approved rate class (Preferred Plus, Preferred, Standard, Table Rating, or Decline). If you received a table rating, ask your agent to shop the case to other carriers — ratings vary significantly.',
        tip: 'A Table 2 rating at one carrier might be Standard at another. Always get a second opinion on substandard offers.',
      },
      {
        id: 'p5-2',
        text: 'Read the policy during your free look period',
        detail: 'Indiana law requires a minimum 10-day free look period. Read the entire policy — especially the exclusions, riders, and beneficiary designations. You can cancel for a full refund during this period.',
      },
      {
        id: 'p5-3',
        text: 'Verify all policy details are correct',
        detail: 'Check: your name (must match ID exactly), date of birth, coverage amount, premium, term length, beneficiary names and percentages, and any riders you requested.',
      },
      {
        id: 'p5-4',
        text: 'Confirm all riders are included as agreed',
        detail: 'Verify that riders you requested (waiver of premium, accelerated death benefit, child term rider, etc.) appear in the policy and at the agreed cost.',
      },
      {
        id: 'p5-5',
        text: 'Set up automatic premium payments',
        detail: 'Set up automatic bank draft to avoid accidental lapse. Indiana carriers offer a 30-day grace period, but a lapsed policy means your family has no coverage.',
        tip: 'Set a calendar reminder 30 days before your annual renewal to review your coverage needs.',
      },
    ],
  },
  {
    id: 'phase6',
    phase: 'Phase 6',
    title: 'After Your Policy Is Active',
    icon: 'ri-calendar-check-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-600',
    items: [
      {
        id: 'p6-1',
        text: 'Store your policy documents in a safe, accessible place',
        detail: 'Keep the original policy in a fireproof safe or safety deposit box. Give your beneficiaries a copy or tell them where to find it. Consider storing a digital copy in a secure cloud service.',
        tip: 'Tell your beneficiaries the carrier name, policy number, and how to file a claim. Many families don\'t know their loved one had a policy.',
      },
      {
        id: 'p6-2',
        text: 'Add your policy to your estate planning documents',
        detail: 'Inform your attorney and financial advisor of the policy. Include it in your will or trust documents. Update your beneficiary designations after major life events.',
      },
      {
        id: 'p6-3',
        text: 'Review your coverage every 2–3 years',
        detail: 'Life changes mean your coverage needs change. Review after: marriage, divorce, birth of a child, home purchase, significant income change, or business ownership.',
        link: { to: '/book-appointment', label: 'Schedule a free coverage review' },
      },
      {
        id: 'p6-4',
        text: 'Update beneficiaries after major life events',
        detail: 'Marriage, divorce, birth of a child, or death of a beneficiary all require a beneficiary update. An outdated beneficiary designation can send your death benefit to the wrong person — or into probate.',
        tip: 'Indiana courts have ruled that a named ex-spouse can still receive the death benefit if the beneficiary designation was never updated after divorce.',
      },
      {
        id: 'p6-5',
        text: 'Consider converting your term policy before the deadline',
        detail: 'If you develop a health condition during your term, exercise your conversion privilege before the deadline. This locks in permanent coverage regardless of your health.',
      },
      {
        id: 'p6-6',
        text: 'Notify your beneficiaries of the policy details',
        detail: 'Make sure your beneficiaries know: the carrier name and phone number, your policy number, and the steps to file a claim. Indiana carriers typically pay claims within 30 days of receiving a completed claim form and death certificate.',
      },
    ],
  },
];

// ─── Lead Capture Form ────────────────────────────────────────────────────────
function LeadCaptureForm({ onUnlock }: { onUnlock: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { setError('Please enter your name and email.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      body.append('name', name);
      body.append('email', email);
      body.append('phone', phone);
      body.append('source', 'Life Insurance Checklist Indiana');
      const res = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      if (res.ok) {
        setSubmitted(true);
        setTimeout(onUnlock, 1200);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-checkbox-circle-line text-3xl" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">You\'re all set!</h3>
        <p className="text-gray-500 text-sm">Unlocking your full checklist now...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
        <input
          type="text"
          name="name"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Sarah Johnson"
          required
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#D97642] transition-colors"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address <span className="text-red-500">*</span></label>
        <input
          type="email"
          name="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="sarah@example.com"
          required
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#D97642] transition-colors"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone <span className="text-gray-400 font-normal">(optional)</span></label>
        <input
          type="tel"
          name="phone"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="(317) 555-0100"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#D97642] transition-colors"
        />
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#D97642] text-white py-3.5 rounded-xl font-bold text-sm hover:bg-[#C4663A] transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap flex items-center justify-center gap-2"
      >
        {submitting ? <><i className="ri-loader-4-line animate-spin" />Sending...</> : <><i className="ri-download-line" />Get My Free Checklist</>}
      </button>
      <p className="text-xs text-gray-400 text-center">No spam, ever. Unsubscribe anytime. Indiana residents only.</p>
    </form>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LifeInsuranceChecklistIndiana() {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [unlocked, setUnlocked] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const totalItems = SECTIONS.reduce((sum, s) => sum + s.items.length, 0);
  const completedCount = checked.size;
  const progressPct = Math.round((completedCount / totalItems) * 100);

  // Free items: Phase 1 + Phase 2 (first 3 items of each)
  const FREE_ITEMS = new Set([
    ...SECTIONS[0].items.map(i => i.id),
    ...SECTIONS[1].items.slice(0, 3).map(i => i.id),
  ]);

  const isLocked = (itemId: string) => !unlocked && !FREE_ITEMS.has(itemId);

  const toggle = (id: string) => {
    if (isLocked(id)) { setShowGate(true); return; }
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handlePrint = () => {
    if (!unlocked) { setShowGate(true); return; }
    window.print();
  };

  const sectionProgress = (section: ChecklistSection) => {
    const done = section.items.filter(i => checked.has(i.id)).length;
    return { done, total: section.items.length, pct: Math.round((done / section.items.length) * 100) };
  };

  useEffect(() => {
    document.title = 'Indiana Life Insurance Checklist — Step-by-Step Buying Guide | Hoosier Life Insurance';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Free printable life insurance checklist for Indiana residents. 35+ steps across 6 phases — from assessing your needs to managing your policy. Download free.');
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'life insurance checklist Indiana, buying life insurance Indiana steps, life insurance guide Indianapolis, Indiana life insurance buying guide');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/resources/life-insurance-checklist-indiana`;

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Buy Life Insurance in Indiana',
        description: 'A complete step-by-step guide for Indiana residents buying life insurance — from assessing your needs to managing your policy.',
        url: `${SITE_URL}/resources/life-insurance-checklist-indiana`,
        totalTime: 'PT2H',
        supply: [{ '@type': 'HowToSupply', name: 'Personal identification' }, { '@type': 'HowToSupply', name: 'Medical history records' }, { '@type': 'HowToSupply', name: 'Financial information' }],
        step: SECTIONS.map((section, si) => ({
          '@type': 'HowToSection',
          name: section.title,
          position: si + 1,
          itemListElement: section.items.map((item, ii) => ({
            '@type': 'HowToStep',
            position: ii + 1,
            name: item.text,
            text: item.detail,
          })),
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Resources', item: `${SITE_URL}/resources` },
          { '@type': 'ListItem', position: 3, name: 'Indiana Life Insurance Checklist', item: `${SITE_URL}/resources/life-insurance-checklist-indiana` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Indiana Life Insurance Buying Checklist',
        url: `${SITE_URL}/resources/life-insurance-checklist-indiana`,
        relatedLink: `${SITE_URL}/blog/life-insurance-checklist-indiana`,
        mentions: {
          '@type': 'Article',
          name: 'Top 10 Life Insurance Checklist Items for Indiana Families',
          url: `${SITE_URL}/blog/life-insurance-checklist-indiana`,
        },
      },
    ];

    schemas.forEach((schema, i) => {
      let script = document.getElementById(`schema-checklist-${i}`) as HTMLScriptElement | null;
      if (!script) { script = document.createElement('script'); script.id = `schema-checklist-${i}`; script.type = 'application/ld+json'; document.head.appendChild(script); }
      script.textContent = JSON.stringify(schema);
    });

    return () => { schemas.forEach((_, i) => document.getElementById(`schema-checklist-${i}`)?.remove()); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 rounded-full bg-[#D97642] blur-3xl" />
          <div className="absolute bottom-0 left-10 w-80 h-80 rounded-full bg-teal-400 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="mb-4">
            <Link to="/resources" className="inline-flex items-center text-white/60 hover:text-white transition-colors text-sm">
              <i className="ri-arrow-left-line mr-2" />Back to Resources
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 text-[#D97642] border border-[#D97642]/30 rounded-full px-4 py-1.5 text-sm font-semibold mb-6">
                <i className="ri-checkbox-multiple-line" />Free Printable Checklist
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
                Indiana Life Insurance<br />Buying Checklist
              </h1>
              <p className="text-lg text-white/75 mb-8 leading-relaxed">
                {totalItems} steps across 6 phases — from calculating your needs to managing your policy. Check off each step as you go.
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-white/60">
                <span className="flex items-center gap-2"><i className="ri-checkbox-line" />{totalItems} actionable steps</span>
                <span className="flex items-center gap-2"><i className="ri-printer-line" />Printable PDF</span>
                <span className="flex items-center gap-2"><i className="ri-map-pin-line" />Indiana-specific</span>
              </div>
            </div>
            {/* Progress Card */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
              <p className="text-white/60 text-xs font-bold uppercase tracking-wider mb-4">Your Progress</p>
              <div className="flex items-end gap-3 mb-4">
                <span className="text-5xl font-bold text-white">{completedCount}</span>
                <span className="text-white/50 text-lg mb-1">/ {totalItems} steps</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-3 mb-3">
                <div
                  className="bg-gradient-to-r from-[#D97642] to-teal-400 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <p className="text-white/60 text-sm">{progressPct}% complete</p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {SECTIONS.map(s => {
                  const { done, total } = sectionProgress(s);
                  return (
                    <div key={s.id} className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-white text-xs font-bold">{done}/{total}</p>
                      <p className="text-white/50 text-xs">{s.phase}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Cross-Link Banner */}
      <div className="bg-[#FDF6F0] border-b border-[#D97642]/20">
        <div className="max-w-5xl mx-auto px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#D97642]/15 text-[#D97642] rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-article-line text-sm" />
            </div>
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-gray-900">Short on time?</span>{' '}
              Read our quick-read breakdown of the{' '}
              <strong>top 10 most important checklist steps</strong> for Indiana families.
            </p>
          </div>
          <Link
            to="/blog/life-insurance-checklist-indiana"
            className="flex-shrink-0 inline-flex items-center gap-1.5 text-[#D97642] font-bold text-sm hover:underline whitespace-nowrap"
          >
            Read the article <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>

      {/* Sticky Progress Bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 py-3 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">{completedCount}/{totalItems} steps</span>
            <div className="flex-1 bg-gray-100 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-[#D97642] to-teal-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="text-sm font-bold text-[#D97642] whitespace-nowrap">{progressPct}%</span>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-[#1A3A52] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#0F2A3F] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-printer-line" />Print Checklist
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12" ref={printRef}>
        <div className="grid lg:grid-cols-3 gap-8">

          {/* Checklist */}
          <div className="lg:col-span-2 space-y-10">
            {SECTIONS.map((section, sIdx) => {
              const { done, total, pct } = sectionProgress(section);
              return (
                <div key={section.id} id={section.id}>
                  {/* Section Header */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-12 h-12 ${section.bgColor} text-white rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <i className={`${section.icon} text-xl`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{section.phase}</span>
                          <h2 className="text-xl font-bold text-gray-900">{section.title}</h2>
                        </div>
                        <span className={`text-sm font-bold ${done === total ? 'text-teal-600' : 'text-gray-400'}`}>{done}/{total}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-500 ${done === total ? 'bg-teal-500' : section.bgColor}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="space-y-3">
                    {section.items.map((item, iIdx) => {
                      const locked = isLocked(item.id);
                      const isChecked = checked.has(item.id);
                      // Show gate prompt after first 3 locked items
                      const isGatePrompt = !unlocked && sIdx === 1 && iIdx === 3;

                      return (
                        <div key={item.id}>
                          {isGatePrompt && (
                            <div className="bg-gradient-to-br from-[#FDF6F0] to-[#FEF3EC] border-2 border-[#D97642] rounded-xl p-6 mb-3">
                              <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-[#D97642] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                  <i className="ri-lock-line text-lg" />
                                </div>
                                <div>
                                  <h3 className="font-bold text-gray-900 mb-1">Unlock the Full 35-Step Checklist</h3>
                                  <p className="text-sm text-gray-600 mb-4">Enter your email to unlock all 6 phases + get a printable PDF version sent to your inbox.</p>
                                  <button
                                    onClick={() => setShowGate(true)}
                                    className="bg-[#D97642] text-white px-5 py-2.5 rounded-lg font-bold text-sm hover:bg-[#C4663A] transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                                  >
                                    <i className="ri-download-line" />Get Free Access
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          <div
                            className={`rounded-xl border transition-all duration-200 ${
                              locked
                                ? 'border-gray-100 bg-gray-50 opacity-60'
                                : isChecked
                                ? 'border-teal-200 bg-teal-50'
                                : 'border-gray-200 bg-white hover:border-[#D97642]/40'
                            }`}
                          >
                            <div
                              className={`flex items-start gap-4 p-5 ${locked ? 'cursor-pointer' : 'cursor-pointer'}`}
                              onClick={() => toggle(item.id)}
                            >
                              {/* Checkbox */}
                              <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                                locked ? 'border-gray-300 bg-gray-100' :
                                isChecked ? 'border-teal-500 bg-teal-500' : 'border-gray-300 hover:border-[#D97642]'
                              }`}>
                                {locked
                                  ? <i className="ri-lock-line text-gray-400 text-xs" />
                                  : isChecked
                                  ? <i className="ri-check-line text-white text-sm" />
                                  : null
                                }
                              </div>

                              <div className="flex-1 min-w-0">
                                <p className={`font-semibold text-sm leading-snug ${isChecked ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                  {item.text}
                                </p>
                                {!locked && (
                                  <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{item.detail}</p>
                                )}
                                {!locked && item.tip && (
                                  <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                    <i className="ri-lightbulb-line text-amber-500 text-sm flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-amber-800">{item.tip}</p>
                                  </div>
                                )}
                                {!locked && item.link && (
                                  <Link
                                    to={item.link.to}
                                    onClick={e => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-xs text-[#D97642] font-semibold mt-2 hover:underline"
                                  >
                                    <i className="ri-external-link-line" />{item.link.label}
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Completion Banner */}
            {completedCount === totalItems && (
              <div className="bg-gradient-to-br from-teal-500 to-emerald-600 text-white rounded-2xl p-8 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-trophy-line text-3xl" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Checklist Complete!</h3>
                <p className="text-white/80 mb-6">You\'ve completed all {totalItems} steps. Your Indiana family is now properly protected.</p>
                <Link to="/book-appointment" className="inline-flex items-center gap-2 bg-white text-teal-700 px-6 py-3 rounded-xl font-bold hover:bg-gray-50 transition-colors whitespace-nowrap">
                  <i className="ri-calendar-line" />Schedule Your Coverage Review
                </Link>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">

            {/* Lead Capture / Unlock Card */}
            {!unlocked ? (
              <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-6 text-white sticky top-24">
                <div className="w-12 h-12 bg-[#D97642] rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-download-line text-2xl" />
                </div>
                <h3 className="text-xl font-bold mb-2">Get the Full Checklist</h3>
                <p className="text-white/70 text-sm mb-5">Unlock all 35 steps + get a printable PDF sent to your inbox. Free for Indiana residents.</p>
                <ul className="space-y-2 mb-6">
                  {['All 6 phases unlocked', 'Printable PDF version', 'Indiana-specific tips', 'No spam, ever'].map(b => (
                    <li key={b} className="flex items-center gap-2 text-sm text-white/80">
                      <i className="ri-check-line text-teal-400" />{b}
                    </li>
                  ))}
                </ul>
                <LeadCaptureForm onUnlock={() => setUnlocked(true)} />
              </div>
            ) : (
              <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6 sticky top-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-teal-500 text-white rounded-xl flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-xl" />
                  </div>
                  <div>
                    <p className="font-bold text-teal-800">Full Access Unlocked</p>
                    <p className="text-xs text-teal-600">All 35 steps available</p>
                  </div>
                </div>
                <button
                  onClick={handlePrint}
                  className="w-full flex items-center justify-center gap-2 bg-[#1A3A52] text-white py-3 rounded-xl font-bold text-sm hover:bg-[#0F2A3F] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-printer-line" />Print Full Checklist
                </button>
              </div>
            )}

            {/* Phase Nav */}
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Jump to Phase</p>
              <div className="space-y-2">
                {SECTIONS.map(section => {
                  const { done, total } = sectionProgress(section);
                  return (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <div className={`w-8 h-8 ${section.bgColor} text-white rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <i className={`${section.icon} text-sm`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 group-hover:text-[#D97642] transition-colors truncate">{section.title}</p>
                        <p className="text-xs text-gray-400">{done}/{total} done</p>
                      </div>
                      {done === total && <i className="ri-check-line text-teal-500 flex-shrink-0" />}
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Quick Tools */}
            <div className="bg-[#F9F7F4] rounded-2xl p-5">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Helpful Tools</p>
              <div className="space-y-3">
                {[
                  { to: '/resources/indiana-life-insurance-calculator', icon: 'ri-calculator-line', label: 'Coverage Calculator', desc: 'Find your exact gap' },
                  { to: '/instant-quote', icon: 'ri-price-tag-3-line', label: 'Instant Quote', desc: 'Compare 5+ carriers' },
                  { to: '/resources/life-insurance-glossary-indiana', icon: 'ri-book-open-line', label: 'Glossary', desc: '55+ terms defined' },
                  { to: '/blog/life-insurance-checklist-indiana', icon: 'ri-article-line', label: 'Top 10 Checklist Items', desc: 'Quick-read summary' },
                  { to: '/book-appointment', icon: 'ri-calendar-line', label: 'Book a Call', desc: 'Free 20-min consult' },
                ].map(tool => (
                  <Link key={tool.to} to={tool.to} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-white transition-colors group">
                    <div className="w-8 h-8 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                      <i className={`${tool.icon} text-sm`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800 group-hover:text-[#D97642] transition-colors">{tool.label}</p>
                      <p className="text-xs text-gray-400">{tool.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Agent CTA */}
            <div className="bg-white border border-gray-200 rounded-2xl p-5 text-center">
              <img
                src="https://public.readdy.ai/ai/img_res/8dedec38bc271f35b0a912abb1b5e73c.jpg"
                alt="Benjamin Wright - Indiana Life Insurance Agent"
                className="w-16 h-16 rounded-full object-cover object-top mx-auto mb-3"
                width={120}
                height={120}
              />
              <p className="font-bold text-gray-900 text-sm">Benjamin Wright</p>
              <p className="text-xs text-gray-500 mb-4">Licensed Indiana Agent</p>
              <a
                href="tel:3176482983"
                className="flex items-center justify-center gap-2 bg-[#1A3A52] text-white py-2.5 rounded-lg font-bold text-sm hover:bg-[#0F2A3F] transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line" />(317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Gate Modal */}
      {showGate && !unlocked && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowGate(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowGate(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer">
              <i className="ri-close-line text-xl" />
            </button>
            <div className="w-14 h-14 bg-[#D97642] text-white rounded-2xl flex items-center justify-center mb-5">
              <i className="ri-download-line text-2xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Unlock the Full Checklist</h3>
            <p className="text-gray-500 text-sm mb-6">Enter your email to unlock all 35 steps and get a printable PDF version — free for Indiana residents.</p>
            <LeadCaptureForm onUnlock={() => { setUnlocked(true); setShowGate(false); }} />
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Need Help Working Through the Checklist?</h2>
          <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">Benjamin Wright walks Indiana families through every step — from calculating your needs to finding the best rate. Free 20-minute consultation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-appointment" className="inline-flex items-center justify-center gap-2 bg-[#D97642] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#C4663A] transition-colors whitespace-nowrap">
              <i className="ri-calendar-line" />Book Free Consultation
            </Link>
            <Link to="/instant-quote" className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-8 py-4 rounded-xl font-bold hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap">
              <i className="ri-price-tag-3-line" />Get Instant Quotes
            </Link>
          </div>
          <p className="mt-6 text-white/50 text-sm">
            Want a quick overview first?{' '}
            <Link to="/blog/life-insurance-checklist-indiana" className="text-[#D97642] font-semibold hover:underline">
              Read our top 10 checklist items article
            </Link>
            {' '}— a 5-minute read.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
